/**
 * Core Layer - Types
 * Framework-agnostic type definitions
 */

/**
 * Endpoint types for differentiating between API call targets
 */
export enum EndpointType {
  APP_API = "app_api", // Call through app API routes
  DIRECT_API = "direct_api", // Call directly to external API
}

/**
 * Standard API response structure
 */
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  response?: T;
  code?: string;
  errors?: Record<string, string[]>;
  meta?: {
    pagination?: PaginationMeta;
  };
}

/**
 * Pagination metadata structure
 */
export interface PaginationMeta {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
  from: number;
  to: number;
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

/**
 * Paginated API response structure
 */
export interface PaginatedApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  response?: {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number;
    to: number;
    links: {
      first: string;
      last: string;
      prev: string | null;
      next: string | null;
    };
  };
}

/**
 * API Error response structure
 */
export interface ApiErrorResponse {
  success: false;
  message: string;
  code?: string;
  response?: {
    message: string;
  } | null;
}

/**
 * Common pagination parameters
 */
export interface PaginationParams {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: Record<string, string | number | boolean>;
}

/**
 * Paginated response structure (domain level)
 */
export interface PaginatedResponse<T> {
  response: T[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
    from: number;
    to: number;
  };
}

/**
 * App config types
 */
export interface AppConfig {
  apiUrl: string;
  appName: string;
  appVersion: string;
  supportEmail: string;
}

/**
 * Theme types
 */
export type Theme = "light" | "dark" | "system";

/**
 * Notification types
 */
export type NotificationType = "info" | "success" | "warning" | "error";

/**
 * Direction types for RTL support
 */
export type Direction = "ltr" | "rtl";

/**
 * Form field validation error (for form libraries)
 */
export interface FieldValidationError {
  message: string;
  type: string;
  ref?: unknown;
}

/**
 * HTTP method types
 */
export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

/**
 * Result type for operations that can fail
 * Used for explicit error handling without exceptions
 */
export type Result<T, E = Error> =
  | { success: true; data: T }
  | { success: false; error: E };

/**
 * Async result type
 */
export type AsyncResult<T, E = Error> = Promise<Result<T, E>>;

/**
 * Pagination hook options
 */
export interface UsePaginationOptions<T> {
  queryKey: readonly unknown[];
  queryFn?: (params: PaginationParams) => Promise<PaginatedResponse<T>>;
  defaultParams?: PaginationParams;
  enabled?: boolean;
}

/**
 * Pagination hook result
 */
export interface UsePaginationResult<T> {
  data: T[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  params: PaginationParams;
  setParams: (params: PaginationParams) => void;
  isEmpty: boolean;
}
